/**
 * Formats a date string or Date object
 * @param {Date|string|number} date - Date to format
 * @param {string} format - Format type ('short', 'long', 'time', 'datetime', 'relative')
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'short', locale = 'en-US') => {
  if (!date) return '';

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date');
  }

  const options = {
    short: { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    },
    long: { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    },
    time: { 
      hour: '2-digit', 
      minute: '2-digit' 
    },
    datetime: { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    }
  };

  if (format === 'relative') {
    return getRelativeTime(dateObj, locale);
  }

  return dateObj.toLocaleString(locale, options[format] || options.short);
};

/**
 * Gets relative time (e.g., "2 hours ago", "in 3 days")
 * @param {Date} date - Date to compare
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date, locale = 'en-US') => {
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSeconds) < 60) {
    return rtf.format(-diffSeconds, 'second');
  } else if (Math.abs(diffMinutes) < 60) {
    return rtf.format(-diffMinutes, 'minute');
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(-diffHours, 'hour');
  } else if (Math.abs(diffDays) < 7) {
    return rtf.format(-diffDays, 'day');
  } else if (Math.abs(diffWeeks) < 4) {
    return rtf.format(-diffWeeks, 'week');
  } else if (Math.abs(diffMonths) < 12) {
    return rtf.format(-diffMonths, 'month');
  } else {
    return rtf.format(-diffYears, 'year');
  }
};

/**
 * Checks if a date is today
 * @param {Date|string|number} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  const dateObj = new Date(date);
  const today = new Date();
  
  return dateObj.toDateString() === today.toDateString();
};

/**
 * Checks if a date is in the past
 * @param {Date|string|number} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPast = (date) => {
  const dateObj = new Date(date);
  return dateObj < new Date();
};

/**
 * Checks if a date is in the future
 * @param {Date|string|number} date - Date to check
 * @returns {boolean} True if date is in the future
 */
export const isFuture = (date) => {
  const dateObj = new Date(date);
  return dateObj > new Date();
};

/**
 * Adds days to a date
 * @param {Date|string|number} date - Original date
 * @param {number} days - Number of days to add
 * @returns {Date} New date
 */
export const addDays = (date, days) => {
  const dateObj = new Date(date);
  return new Date(dateObj.getTime() + (days * 24 * 60 * 60 * 1000));
};

/**
 * Gets the start of day for a given date
 * @param {Date|string|number} date - Date
 * @returns {Date} Start of day
 */
export const startOfDay = (date) => {
  const dateObj = new Date(date);
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
};

/**
 * Gets the end of day for a given date
 * @param {Date|string|number} date - Date
 * @returns {Date} End of day
 */
export const endOfDay = (date) => {
  const dateObj = new Date(date);
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 23, 59, 59, 999);
};
